/**
 * Load a photo's bytes for CANVAS work (copy-to-clipboard, rotate) without
 * ever loading the remote url into an <img> in CORS mode.
 *
 * Why this exists — the CORS cache trap (2026-10-08):
 *   Chrome's HTTP cache keys on URL, not on request mode. A plain <img> load
 *   of a CloudFront/S3 photo sends no Origin header, so S3 answers with NO
 *   Access-Control-Allow-Origin and NO Vary: Origin, and that response is
 *   cached. A later CORS-mode load of the SAME url (an <img crossOrigin> or a
 *   `new Image()` with crossOrigin set) is served that cached entry, fails the
 *   CORS check and the photo breaks — "Provisional headers" in DevTools,
 *   because nothing reached the network. Every enlarge (ImageModal) is a plain
 *   <img>, so this bit the Single Client tiles after a calendar enlarge. The
 *   poisoned entry lives as long as Chrome's heuristic freshness (the objects
 *   carry no Cache-Control): minutes for a new photo, days for an old one.
 *
 *   The rule, enforced by `__tests__/mediaTests/noCrossOriginImg.test.js`:
 *   display tiles are plain <img> (never crossOrigin) and anything that needs
 *   pixels comes here.
 *     fetch(src, { mode: "cors", cache: "no-store" }) — a real CORS request
 *       that carries Origin, so S3/CloudFront answer WITH the ACAO header.
 *       `no-store` means the poisoned entry is never consulted AND the display
 *       entry is never overwritten (a `reload` would, and the next plain
 *       display load would refetch on the Vary mismatch — ping-pong).
 *     → Blob → object url → new Image(): blob: urls are same-origin, so the
 *       canvas never taints, and the decode path is the same HTMLImageElement
 *       path the old crossOrigin Image used (orientation behaviour unchanged).
 *
 *   Works unchanged for the private bucket's SIGNED urls (the url is passed
 *   through untouched, so the signature stays valid — a cache-busting query
 *   param would have broken it), for the same-origin `/ba-image/:id` redirect
 *   shim, and for data: URIs (local just-uploaded previews).
 *
 * Resolves the loaded HTMLImageElement. REJECTS on a non-OK response, a fetch
 * failure (a CORS failure surfaces as a TypeError) or a decode error. The
 * object url is revoked either way — the decoded image survives the revoke.
 */
export const FETCH_IMAGE_OPTS = Object.freeze({
  mode: "cors",
  cache: "no-store",
  // Same as the old anonymous-mode Image: cookies only to our own origin (the
  // /ba-image shim, which is unauthenticated anyway), never to the CDN / S3.
  credentials: "same-origin",
});

const fetchImageForCanvas = async (src) => {
  if (!src) throw new Error("fetchImageForCanvas: no src");

  const res = await fetch(src, FETCH_IMAGE_OPTS);
  if (!res.ok) {
    throw new Error(
      `fetchImageForCanvas: HTTP ${res.status} for ${String(src).slice(0, 120)}`
    );
  }

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error("fetchImageForCanvas: image decode failed"));
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export default fetchImageForCanvas;
